import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronDown, Loader2, Clock, CalendarDays, Image as ImageIcon, X } from 'lucide-react';
import { toZonedTime } from 'date-fns-tz';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import StepIndicator from './StepIndicator';
import StripeCardInput from './StripeCardInput';
import { updateUserProfile } from '../../services/authService';

const isDevMode = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const stripePromise = isDevMode ? Promise.resolve(null) : loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const SingleConfirmStyles = () => (
  <style>{`
    .confirm-page {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 clamp(10px, 2vw, 20px);
      font-family: 'Plus Jakarta Sans', 'Noto Sans TC', sans-serif;
      overflow-x: visible;
    }
    .confirm-container {
      display: grid;
      grid-template-columns: 1.5fr 1fr;
      row-gap: 0;
      column-gap: clamp(20px, 3vw, 30px);
    }
    .confirm-fulfillment {
      grid-column: 1;
      background: #fff;
      border-radius: 4px 4px 0 0;
      padding: clamp(20px, 4vw, 40px);
      padding-bottom: 0;
      box-shadow: 0 -2px 20px rgba(0,0,0,0.06);
      min-width: 0;
    }
    .confirm-main {
      grid-column: 1;
      background: #fff;
      border-radius: 0 0 4px 4px;
      padding: clamp(20px, 4vw, 40px);
      padding-top: 24px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.06);
      min-width: 0;
      border-top: 1px solid #eee;
    }
    .confirm-section-title {
      font-size: clamp(1rem, 3vw, 1.1rem);
      font-weight: 400;
      color: #1a1a1a;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 1px solid #f1f5f9;
    }
    .section-divider {
      height: 1px;
      background-color: #e8e8e8;
      margin: 35px 0;
      border: none;
    }

    /* Fulfillment toggle */
    .fulfillment-toggle {
      display: flex;
      gap: 0;
      background: #fff;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      padding: 4px;
      margin-bottom: 20px;
      overflow: hidden;
    }
    .fulfillment-btn {
      flex: 1;
      padding: 12px 20px;
      border: none;
      border-radius: 4px;
      font-size: 0.95rem;
      font-weight: 400;
      cursor: pointer;
      transition: all 0.2s ease;
      background: transparent;
      color: #666;
      font-family: inherit;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    }
    .fulfillment-btn.active {
      background: #2F7D5E;
      color: #fff;
      box-shadow: 0 2px 8px rgba(47,125,94,0.2);
    }
    .fulfillment-btn:not(.active):hover {
      background: #f5f5f5;
      color: #333;
    }

    /* Time slots */
    .time-slots-section {
      margin-top: 16px;
      margin-bottom: 20px;
    }
    .time-slots-label {
      font-size: 0.85rem;
      font-weight: 400;
      color: #555;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .time-select {
      max-width: 280px;
      appearance: auto;
      cursor: pointer;
    }

    .readonly-address {
      font-size: 0.9rem;
      color: #999;
      padding: 10px 14px;
      background: #f8f8f8;
      border-radius: 4px;
      border: 1px solid #eee;
    }

    /* Form */
    .confirm-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .form-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .form-label {
      font-size: 0.8rem;
      font-weight: 400;
      color: #333;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .form-input, .form-textarea {
      padding: 14px 16px;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      font-size: 16px;
      font-family: inherit;
      transition: all 0.3s ease;
      background: #fff;
      width: 100%;
      box-sizing: border-box;
    }
    .form-textarea {
      min-height: 80px;
      resize: vertical;
    }
    .form-input:focus, .form-textarea:focus {
      outline: none;
      border-color: #2F7D5E;
      box-shadow: 0 0 0 3px rgba(47, 125, 94, 0.15);
    }
    .form-input:disabled, .form-textarea:disabled {
      background: #f5f5f5;
      cursor: not-allowed;
    }
    .form-input:-webkit-autofill,
    .form-input:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px #fff inset;
      box-shadow: 0 0 0 1000px #fff inset;
      -webkit-text-fill-color: inherit;
    }
    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }

    /* Actions */
    .confirm-actions {
      display: flex;
      gap: 15px;
      margin-top: 20px;
      flex-wrap: wrap;
    }
    .confirm-btn {
      padding: 16px 40px;
      border: 1px solid transparent;
      font-size: 16px;
      font-weight: 400;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      border-radius: 4px;
      flex: 1;
      min-width: 140px;
      font-family: inherit;
      box-sizing: border-box;
    }
    .confirm-btn-primary {
      background: #2F7D5E;
      color: #fff;
      border-color: #2F7D5E;
      box-shadow: 0 4px 12px rgba(47,125,94,0.2);
    }
    .confirm-btn-primary:hover { background: #245F48; border-color: #245F48; }
    .confirm-btn-primary:disabled { background: #cbd5e1; border-color: #cbd5e1; cursor: not-allowed; box-shadow: none; }
    .confirm-btn-secondary {
      background: #fff;
      color: #2F7D5E;
      border-color: #e0e0e0;
    }

    /* Order summary sidebar */
    .order-summary-sidebar {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 4px;
      padding: clamp(20px, 4vw, 30px);
      height: fit-content;
      box-shadow: 0 4px 20px rgba(0,0,0,0.04);
      min-width: 0;
      grid-column: 2;
      grid-row: 1 / 3;
    }
    .summary-title {
      font-size: 1.1rem;
      font-weight: 400;
      color: #2F7D5E;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 1px solid #f1f5f9;
    }
    .summary-items { margin-bottom: 20px; max-height: 350px; overflow-y: auto; }
    .summary-cart-item {
      display: grid;
      grid-template-columns: 42px 1fr;
      gap: 12px;
      padding: 12px 0;
      border-bottom: 1px solid #f5f5f5;
      align-items: center;
    }
    .summary-cart-item:last-child { border-bottom: none; }
    .summary-item-img {
      width: 42px; height: 42px;
      border-radius: 4px;
      object-fit: cover;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
    }
    .summary-item-info { min-width: 0; }
    .summary-item-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 8px;
    }
    .summary-item-name {
      font-size: 0.85rem;
      font-weight: 400;
      color: #1e293b;
      line-height: 1.3;
    }
    .summary-item-qty {
      font-size: 0.85rem;
      color: #94a3b8;
    }
    .summary-item-price {
      font-size: 0.85rem;
      font-weight: 400;
      color: #1e293b;
      white-space: nowrap;
    }
    .summary-item-mods {
      font-size: 0.78rem;
      color: #64748b;
      margin-top: 2px;
    }
    .summary-item-note {
      font-size: 0.78rem;
      color: #b45309;
      margin-top: 2px;
      font-style: italic;
    }
    .summary-totals {
      border-top: 1px solid #e0e0e0;
      padding-top: 16px;
    }
    .summary-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
      font-size: 0.9rem;
      color: #666;
    }
    .summary-row.fee-row {
      margin-bottom: 12px;
      padding-bottom: 12px;
      border-bottom: 1px dashed #e0e0e0;
    }
    .summary-row.tax-row { font-size: 0.85rem; color: #888; }
    .summary-total {
      display: flex;
      justify-content: space-between;
      font-size: 1.3rem;
      font-weight: 400;
      color: #1a1a1a;
      margin-top: 15px;
      padding-top: 15px;
      border-top: 1px solid #ccc;
    }

    .spinner { animation: spin 1s linear infinite; }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

    @media (max-width: 1024px) {
      .confirm-container { grid-template-columns: 1fr; row-gap: clamp(16px, 3vw, 24px); }
      .confirm-fulfillment { grid-column: 1; order: 0; border-radius: 4px; padding-bottom: clamp(20px, 4vw, 40px); box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
      .order-summary-sidebar { grid-column: 1; grid-row: auto; position: static; order: 1; }
      .confirm-main { grid-column: 1; order: 2; border-radius: 4px; border-top: none; padding-top: clamp(20px, 4vw, 40px); }
    }
    /* Date chips horizontal scroll */
    .date-chips-scroll {
      display: flex; gap: 10px;
      overflow-x: auto; padding: 4px 0 12px;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .date-chips-scroll::-webkit-scrollbar { display: none; }

    .date-chip {
      flex-shrink: 0;
      min-width: 80px; padding: 12px 16px;
      border: 1px solid #e8e8e8; border-radius: 4px;
      background: #fff; cursor: pointer;
      text-align: center; transition: all 0.2s;
    }
    .date-chip .chip-weekday { font-size: 0.8rem; color: #888; margin-bottom: 4px; }
    .date-chip .chip-date { font-size: 1rem; font-weight: 400; color: #333; }

    .date-chip.active {
      border-color: #2F7D5E; background: #f0faf5;
    }
    .date-chip.active .chip-date { color: #2F7D5E; }

    .no-slots-msg {
      text-align: center; color: #999; font-size: 0.9rem;
      padding: 20px 0;
    }

    @media (max-width: 640px) {
      .confirm-page { padding: 0 8px; }
      .confirm-fulfillment { padding: 16px; border-radius: 4px !important; }
      .confirm-main { padding: 16px; border-radius: 4px !important; }
      .confirm-section-title { font-size: 0.95rem; margin-bottom: 14px; padding-bottom: 10px; }
      .section-divider { margin: 20px 0; }

      .fulfillment-toggle { padding: 3px; margin-bottom: 14px; }
      .fulfillment-btn { padding: 10px 8px; font-size: 0.85rem; }

      .date-chip { min-width: 68px; padding: 10px 10px; }
      .date-chip .chip-weekday { font-size: 0.72rem; }
      .date-chip .chip-date { font-size: 0.9rem; }

      .time-select { max-width: 100%; }

      .form-row { grid-template-columns: 1fr; }
      .form-input, .form-textarea { padding: 12px 14px; font-size: 16px; }
      .confirm-actions { flex-direction: column; gap: 10px; }
      .confirm-btn { width: 100%; padding: 14px 20px; font-size: 15px; }

      .order-summary-sidebar { padding: 16px; border-radius: 4px; }
      .summary-total { font-size: 1.1rem; }
    }
    @media (min-width: 1025px) {
      .order-summary-sidebar { position: sticky; top: 90px; }
    }

    /* Time Picker Bottom Sheet */
    .time-picker-trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 16px;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      font-size: 16px;
      font-family: inherit;
      background: #fff;
      cursor: pointer;
      transition: all 0.3s ease;
      max-width: 280px;
      width: 100%;
      box-sizing: border-box;
      text-align: left;
    }
    .time-picker-trigger:hover {
      border-color: #ccc;
    }
    .time-picker-trigger .trigger-text {
      color: #999;
    }
    .time-picker-trigger .trigger-text.has-value {
      color: #1a1a1a;
      font-weight: 400;
    }
    .time-picker-trigger .trigger-icon {
      color: #999;
      flex-shrink: 0;
    }

    .tp-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.4);
      z-index: 2000;
      animation: tpFadeIn 0.25s ease;
    }
    .tp-sheet {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: #fff;
      border-radius: 4px 4px 0 0;
      max-height: 60vh;
      z-index: 2001;
      display: flex;
      flex-direction: column;
      animation: tpSlideUp 0.3s ease;
    }
    .tp-handle {
      width: 36px;
      height: 4px;
      background: #ddd;
      border-radius: 2px;
      margin: 12px auto 0;
    }
    .tp-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px 12px;
      flex-shrink: 0;
    }
    .tp-header-title {
      font-size: 1.05rem;
      font-weight: 400;
      color: #1a1a1a;
    }
    .tp-close {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: none;
      background: #f0f0f0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s;
      flex-shrink: 0;
    }
    .tp-close:hover {
      background: #e0e0e0;
    }
    .tp-body {
      padding: 4px 20px 24px;
      overflow-y: auto;
      flex: 1;
    }
    .tp-grid {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .tp-slot {
      width: 100%;
      padding: 14px 16px;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      background: #fff;
      font-size: 0.95rem;
      font-weight: 400;
      color: #333;
      cursor: pointer;
      transition: all 0.2s ease;
      text-align: left;
      font-family: inherit;
      box-sizing: border-box;
    }
    .tp-slot:hover {
      border-color: #ccc;
      background: #fafafa;
    }
    .tp-slot.active {
      border-color: #2F7D5E;
      background: #f0faf5;
      color: #2F7D5E;
      font-weight: 400;
    }

    @keyframes tpFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes tpSlideUp {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }

    @media (max-width: 640px) {
      .time-picker-trigger { max-width: 100%; }
      .tp-sheet { max-height: 70vh; }
    }
  `}</style>
);

// Build "HH:MM - HH:MM+30min" range label from a time string
const toRangeLabel = (timeStr) => {
  const [h, m] = timeStr.split(':').map(Number);
  const endM = m + 30;
  const endH = endM >= 60 ? h + 1 : h;
  const start = `${h}:${String(m).padStart(2, '0')}`;
  const end = `${endH}:${String(endM % 60).padStart(2, '0')}`;
  return `${start} - ${end}`;
};

// Generate pickup time slots (every 30 min from 11:00 to 21:00) with range labels
const generatePickupSlots = () => {
  const slots = [];
  const addSlot = (h, m) => {
    const start = `${h}:${String(m).padStart(2, '0')}`;
    const endM = m + 30;
    const endH = endM >= 60 ? h + 1 : h;
    const end = `${endH}:${String(endM % 60).padStart(2, '0')}`;
    slots.push({ id: `pickup_${h}_${String(m).padStart(2, '0')}`, time: start, label: `${start} - ${end}` });
  };
  for (let h = 11; h <= 20; h++) {
    addSlot(h, 0);
    addSlot(h, 30);
  }
  addSlot(21, 0); // 21:00 - 21:30
  return slots;
};

const PICKUP_TIME_SLOTS = generatePickupSlots();

const SingleConfirmPageInner = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('booking');

  const {
    bookingMode,
    cartItems,
    orderType,
    fulfillmentMode, setFulfillmentMode,
    scheduledTime, setScheduledTime,
    scheduledDate, setScheduledDate,
    next7Days,
    matchedZone,
    handlePayment,
    user,
    isLoggedIn,
    requestLogin,
    deliveryAddress,
    unitNumber,
    city,
    postalCode,
    province,
    deliveryNotes, setDeliveryNotes,
    menuOptions,
    globalDiscount,
    packagingFee,
    needCutlery,
  } = useOutletContext();

  const discountEnabled = globalDiscount?.enabled && globalDiscount?.rate && globalDiscount.rate < 1;
  const discountRate = discountEnabled ? globalDiscount.rate : 1;

  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false); // prevent double-charge
  const [showTimePicker, setShowTimePicker] = useState(false);

  // Scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Lock body scroll when time picker is open
  useEffect(() => {
    if (showTimePicker) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showTimePicker]);

  // Close on ESC
  useEffect(() => {
    if (!showTimePicker) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') setShowTimePicker(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showTimePicker]);

  const handleSlotSelect = useCallback((time) => {
    setScheduledTime(time);
    setShowTimePicker(false);
  }, [setScheduledTime]);

  // Contact info: prefer user profile from DB (cross-device), fallback to localStorage
  let savedContact = null;
  try { savedContact = JSON.parse(localStorage.getItem('saved_contact') || 'null'); } catch { /* ignore */ }
  const dbName = user?.name || user?.user_metadata?.name || '';
  const dbPhone = user?.phone_number || user?.user_metadata?.phone || '';
  const [saveContact, setSaveContact] = useState(!!savedContact || !!(dbName && dbPhone));
  const stripe = useStripe();
  const elements = useElements();
  const [stripeError, setStripeError] = useState(null);

  const [formData, setFormData] = useState({
    contactName: savedContact?.name || dbName || '',
    contactPhone: savedContact?.phone || dbPhone || '',
    deliveryNotes: '',
    cardHolder: '',
    email: '',
  });

  // Guard: redirect if cart empty
  useEffect(() => {
    if (!cartItems || cartItems.length === 0) {
      navigate('../meals', { replace: true });
    }
  }, [cartItems, navigate]);

  // Auto-fill user data (localStorage takes priority, then user metadata)
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        contactName: prev.contactName || user.user_metadata?.name || '',
        contactPhone: prev.contactPhone || user.user_metadata?.phone || '',
        email: user.email || prev.email,
      }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleSaveContact = () => {
    const next = !saveContact;
    setSaveContact(next);
    if (!next) {
      localStorage.removeItem('saved_contact');
    }
  };

  // Available time slots — single mode uses same 30-min slots for both pickup and delivery
  // Single mode: same 30-min slots (11:00–21:00) for both pickup and delivery
  const availableTimeSlots = PICKUP_TIME_SLOTS;

  // Filter out past time slots when today is selected
  const filteredSlots = useMemo(() => {
    if (!scheduledDate) return [];
    const todayStr = next7Days[0]?.isoDateStr?.slice(0, 10);
    const isToday = scheduledDate === todayStr;
    if (!isToday) return availableTimeSlots;

    const nowVan = toZonedTime(new Date(), 'America/Vancouver');
    const nowMinutes = nowVan.getHours() * 60 + nowVan.getMinutes();
    return availableTimeSlots.filter(slot => {
      // Handle both "HH:MM" and "HH:MM-HH:MM" formats — use start time
      const startTime = slot.time.split('-')[0];
      const [h, m] = startTime.split(':').map(Number);
      return h * 60 + m > nowMinutes + 30; // at least 30 min from now
    });
  }, [scheduledDate, availableTimeSlots, next7Days]);

  // Cart totals
  const orderBreakdown = useMemo(() => {
    let originalSubtotal = 0;
    let subtotal = 0;
    let gst = 0;
    let pst = 0;

    cartItems.forEach(item => {
      const modTotal = item.modifiers.reduce((s, m) => s + (m.price || 0), 0);
      const itemOriginal = (item.price + modTotal) * item.quantity;
      const itemTotal = itemOriginal * discountRate;
      originalSubtotal += itemOriginal;
      subtotal += itemTotal;
      if (item.tax_gst) gst += itemTotal * 0.05;
      if (item.tax_pst) pst += itemTotal * 0.07;
    });

    let deliveryFee = 0;
    if (orderType === 'delivery' && matchedZone) {
      const rawFee = matchedZone.delivery_fee ?? matchedZone.deliveryFee ?? 0;
      deliveryFee = Number(rawFee);
    }

    const discount = discountEnabled ? originalSubtotal - subtotal : 0;
    const pkgFee = packagingFee || 0;

    return {
      originalSubtotal: discountEnabled ? originalSubtotal : subtotal,
      subtotal,
      discount,
      gst,
      pst,
      packagingFee: pkgFee,
      deliveryFee,
      grandTotal: subtotal + gst + pst + pkgFee + deliveryFee,
      ...(discountEnabled ? {
        discountRate: globalDiscount.rate,
        discountLabel: globalDiscount.label || '',
      } : {}),
    };
  }, [cartItems, matchedZone, orderType, discountRate, discountEnabled, globalDiscount, packagingFee]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isProcessing) return; // prevent double-submission
    setStripeError(null);

    // 檢查購物車是否有售罄商品
    const soldOutItems = cartItems.filter(item => {
      const menuItem = menuOptions.find(m => m.id === item.menuItemId);
      return menuItem && menuItem.is_available === false;
    });
    if (soldOutItems.length > 0) {
      const names = soldOutItems.map(i => i.name).join('、');
      alert(t('single_confirm.sold_out_alert', '以下商品已售罄，無法結帳：\n{{names}}\n\n請返回購物車移除這些商品。', { names }));
      return;
    }

    if (!formData.contactName || !formData.contactPhone) {
      alert(t('payment.alert_contact'));
      return;
    }
    // Save contact info: store in DB (cross-device) + localStorage (fallback)
    if (saveContact) {
      localStorage.setItem('saved_contact', JSON.stringify({ name: formData.contactName, phone: formData.contactPhone }));
      if (user?.id) {
        updateUserProfile(user.id, { name: formData.contactName, phone_number: formData.contactPhone }).catch(() => {});
      }
    }
    if (!isDevMode && !formData.cardHolder) {
      alert(t('payment.alert_payment'));
      return;
    }
    if (!isDevMode && (!stripe || !elements)) {
      return;
    }
    if (fulfillmentMode === 'scheduled' && (!scheduledDate || !scheduledTime)) {
      alert(t('single_confirm.alert_select_date_time', '請選擇預約日期和時間'));
      return;
    }
    // Validate delivery address before payment
    if (orderType === 'delivery' && (!deliveryAddress || !city)) {
      alert(t('errors.missing_address', '請填寫完整的配送地址'));
      return;
    }

    if (!isLoggedIn) {
      // Save cart to localStorage before login to prevent data loss
      localStorage.setItem('pending_cart', JSON.stringify(cartItems));
      localStorage.setItem('pending_order_type', orderType);
      requestLogin();
      return;
    }

    setIsProcessing(true);
    try {
      if (isDevMode) {
        // Dev mode: skip Stripe entirely, create order directly
        await handlePayment(formData, orderBreakdown, 'dev_test_' + Date.now());
      } else {
        // 1. Create PaymentIntent with server-side price verification
        const cartDataForServer = cartItems.map(item => ({
          id: item.menuItemId,
          quantity: item.quantity,
          modifier_ids: item.modifiers.map(m => m.id),
        }));
        const amountInCents = Math.round(orderBreakdown.grandTotal * 100);
        const res = await fetch('/api/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mode: 'single',
            cart_items: cartDataForServer,
            order_type: orderType,
            zone_id: matchedZone?.id || null,
            amount: amountInCents,
            metadata: { contact_name: formData.contactName, contact_phone: formData.contactPhone },
          }),
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.error || `Payment API error (${res.status})`);
        }
        const { clientSecret, error: apiError } = await res.json();
        if (apiError || !clientSecret) {
          throw new Error(apiError || 'Failed to create payment intent');
        }

        // 2. Confirm card payment with Stripe
        const { error: stripeErr, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement('cardNumber'),
            billing_details: { name: formData.cardHolder },
          },
        });

        if (stripeErr) {
          setStripeError(stripeErr.message);
          setIsProcessing(false);
          return;
        }

        // 3. Payment succeeded — mark to prevent re-submission
        setPaymentSucceeded(true);

        // 4. Verify payment server-side before creating order
        const verifyRes = await fetch('/api/verify-payment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payment_intent_id: paymentIntent.id }),
        });
        const verifyData = await verifyRes.json();
        if (!verifyData.verified) {
          throw new Error('Payment verification failed. Please contact support.');
        }

        // 5. Create order (payment verified)
        await handlePayment(formData, orderBreakdown, paymentIntent.id);
      }
    } catch (err) {
      if (paymentSucceeded) {
        // Payment was charged but order creation failed — do NOT allow retry
        setStripeError(t('payment.error_order_failed', '付款已成功，但訂單建立失敗。請聯繫客服處理，勿重新付款。'));
      } else {
        setStripeError(err.message);
        setIsProcessing(false);
      }
    }
  };

  if (!cartItems || cartItems.length === 0) return null;

  const instantLabel = orderType === 'pickup'
    ? t('single_confirm.instant_pickup', '即時取餐')
    : t('single_confirm.instant_delivery', '即時配送');
  const scheduledLabel = orderType === 'pickup'
    ? t('single_confirm.scheduled_pickup', '預約取餐')
    : t('single_confirm.scheduled_delivery', '預約配送');

  return (
    <>
      <SingleConfirmStyles />
      <StepIndicator currentStep={2} bookingMode={bookingMode} />

      <div className="confirm-page">
        <div className="confirm-container">
          {/* Fulfillment section - own block for mobile ordering */}
          <div className="confirm-fulfillment">
            <div className="fulfillment-toggle">
              <button
                className={`fulfillment-btn ${fulfillmentMode === 'instant' ? 'active' : ''}`}
                onClick={() => { setFulfillmentMode('instant'); setScheduledTime(null); setScheduledDate(null); }}
              >
                {instantLabel}
              </button>
              <button
                className={`fulfillment-btn ${fulfillmentMode === 'scheduled' ? 'active' : ''}`}
                onClick={() => setFulfillmentMode('scheduled')}
              >
                {scheduledLabel}
              </button>
            </div>

            {/* Date + Time selection for scheduled */}
            {fulfillmentMode === 'scheduled' && (
              <>
                {/* Date chips */}
                <div className="time-slots-section">
                  <div className="time-slots-label">
                    <CalendarDays size={16} />
                    {t('single_confirm.select_date', '選擇日期')}
                  </div>
                  <div className="date-chips-scroll">
                    {next7Days.map((day, idx) => {
                      const dateKey = day.isoDateStr.slice(0, 10);
                      const isActive = scheduledDate === dateKey;
                      let chipLabel = day.weekday;
                      if (idx === 0) chipLabel = t('single_confirm.today', '今天');
                      else if (idx === 1) chipLabel = t('single_confirm.tomorrow', '明天');
                      return (
                        <div
                          key={day.id}
                          className={`date-chip ${isActive ? 'active' : ''}`}
                          onClick={() => {
                            setScheduledDate(dateKey);
                            setScheduledTime(null); // reset time when date changes
                          }}
                        >
                          <div className="chip-weekday">{chipLabel}</div>
                          <div className="chip-date">{day.dateString}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Time dropdown (only after date is selected) */}
                {scheduledDate && (
                  <div className="time-slots-section">
                    <div className="time-slots-label">
                      <Clock size={16} />
                      {t('single_confirm.select_time', '選擇時間')}
                    </div>
                    {filteredSlots.length > 0 ? (
                      <>
                        <button
                          type="button"
                          className="time-picker-trigger"
                          onClick={() => setShowTimePicker(true)}
                        >
                          <span className={`trigger-text ${scheduledTime ? 'has-value' : ''}`}>
                            {(scheduledTime && filteredSlots.find(s => s.time === scheduledTime)?.label) || t('single_confirm.select_time_placeholder', '-- 請選擇時間 --')}
                          </span>
                          <ChevronDown size={18} className="trigger-icon" />
                        </button>

                        {showTimePicker && (
                          <>
                            <div className="tp-overlay" onClick={() => setShowTimePicker(false)} />
                            <div className="tp-sheet">
                              <div className="tp-handle" />
                              <div className="tp-header">
                                <span className="tp-header-title">
                                  {t('single_confirm.select_time', '選擇時間')}
                                </span>
                                <button
                                  type="button"
                                  className="tp-close"
                                  onClick={() => setShowTimePicker(false)}
                                >
                                  <X size={16} />
                                </button>
                              </div>
                              <div className="tp-body">
                                <div className="tp-grid">
                                  {filteredSlots.map(slot => (
                                    <button
                                      type="button"
                                      key={slot.id}
                                      className={`tp-slot ${scheduledTime === slot.time ? 'active' : ''}`}
                                      onClick={() => handleSlotSelect(slot.time)}
                                    >
                                      {slot.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="no-slots-msg">
                        {t('single_confirm.no_slots_today', '今天已無可選時段')}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

          </div>

          {/* Left: Form */}
          <div className="confirm-main">
            {/* Contact info */}
            <form className="confirm-form" onSubmit={handleSubmit}>
              <h2 className="confirm-section-title">{t('payment.contact_title')}</h2>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('payment.contact_name')}</label>
                  <input
                    className="form-input"
                    type="text"
                    name="contactName"
                    placeholder={t('payment.contact_name_placeholder')}
                    value={formData.contactName}
                    onChange={handleInputChange}
                    required
                    disabled={isProcessing}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('payment.contact_phone')}</label>
                  <input
                    className="form-input"
                    type="tel"
                    name="contactPhone"
                    placeholder={t('payment.contact_phone_placeholder')}
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    required
                    disabled={isProcessing}
                  />
                </div>
              </div>

              <label className="save-contact-check" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#475569', marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={saveContact}
                  onChange={toggleSaveContact}
                  style={{ width: 18, height: 18, accentColor: '#2F7D5E', cursor: 'pointer' }}
                />
                {t('payment.save_contact', '記住聯絡資料，下次自動填入')}
              </label>

              {orderType === 'delivery' && deliveryAddress && (
                <div className="form-group">
                  <label className="form-label">{t('payment.delivery_address', '配送地址')}</label>
                  <div className="readonly-address">
                    {deliveryAddress}{unitNumber ? ` #${unitNumber}` : ''}, {city}, {province} {postalCode}
                  </div>
                </div>
              )}

              {orderType === 'delivery' && (
                <div className="form-group">
                  <label className="form-label">{t('payment.delivery_notes')}</label>
                  <textarea
                    className="form-textarea"
                    placeholder={t('payment.delivery_notes_placeholder')}
                    value={deliveryNotes || ''}
                    onChange={(e) => setDeliveryNotes(e.target.value)}
                    disabled={isProcessing}
                  />
                </div>
              )}

              {/* Payment info */}
              {isDevMode ? (
                <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 4, padding: '12px 16px', marginBottom: 16, fontSize: '0.85rem', color: '#92400e' }}>
                  DEV MODE — 跳過 Stripe 付款，直接下單
                </div>
              ) : (
                <>
                  <h2 className="confirm-section-title">{t('payment.payment_title')}</h2>

                  <div className="form-group">
                    <label className="form-label">{t('payment.card_holder')}</label>
                    <input
                      className="form-input"
                      type="text"
                      name="cardHolder"
                      placeholder={t('payment.card_holder_placeholder')}
                      value={formData.cardHolder}
                      onChange={handleInputChange}
                      required
                      disabled={isProcessing}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('payment.card_number', '卡號')}</label>
                    <StripeCardInput disabled={isProcessing} />
                    {stripeError && (
                      <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: 4 }}>{stripeError}</div>
                    )}
                  </div>
                </>
              )}

              <div className="form-group" style={{display:'none'}}>
                <label className="form-label">{t('payment.email')}</label>
                <input
                  className="form-input"
                  type="email"
                  name="email"
                  placeholder="example@mail.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isProcessing}
                />
              </div>

              <div className="confirm-actions">
                <button
                  type="button"
                  onClick={() => navigate('../meals')}
                  className="confirm-btn confirm-btn-secondary"
                  disabled={isProcessing}
                >
                  <ChevronLeft size={18} /> {t('payment.back')}
                </button>
                <button
                  type="submit"
                  className="confirm-btn confirm-btn-primary"
                  disabled={isProcessing}
                  style={{opacity: isProcessing ? 0.7 : 1}}
                >
                  {isProcessing ? (
                    <><Loader2 className="spinner" size={20} /> {t('payment.processing')}</>
                  ) : isDevMode ? (
                    `測試下單 CAD$ ${orderBreakdown.grandTotal.toFixed(2)}`
                  ) : (
                    t('payment.confirm_pay', { amount: orderBreakdown.grandTotal.toFixed(2) })
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Right: Order summary */}
          <div className="order-summary-sidebar">
            <h3 className="summary-title">{t('payment.order_summary')}</h3>

            <div className="summary-items">
              {cartItems.map(item => {
                const modTotal = item.modifiers.reduce((s, m) => s + (m.price || 0), 0);
                const lineOriginal = (item.price + modTotal) * item.quantity;
                const lineTotal = lineOriginal * discountRate;
                const menuItem = menuOptions.find(m => m.id === item.menuItemId);
                const isSoldOut = menuItem && menuItem.is_available === false;
                return (
                  <div key={item.cartId} className="summary-cart-item" style={isSoldOut ? {background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'4px', padding:'8px'} : {}}>
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="summary-item-img" loading="lazy" decoding="async" />
                    ) : (
                      <div className="summary-item-img" style={{display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <ImageIcon size={16} color="#ccc" />
                      </div>
                    )}
                    <div className="summary-item-info">
                      <div className="summary-item-row">
                        <div className="summary-item-name">
                          {item.name}
                          {isSoldOut && <span style={{background:'#ef4444', color:'white', fontSize:'0.7rem', padding:'2px 6px', borderRadius:'4px', fontWeight:400, marginLeft:'6px'}}>售罄</span>}
                          <span className="summary-item-qty">x{item.quantity}</span>
                        </div>
                        <span className="summary-item-price">
                          {discountEnabled && <span style={{textDecoration:'line-through', color:'#999', fontSize:'0.85em', marginRight:4}}>${lineOriginal.toFixed(2)}</span>}
                          ${lineTotal.toFixed(2)}
                        </span>
                      </div>
                      {item.modifiers.length > 0 && (
                        <div className="summary-item-mods">
                          {item.modifiers.map((m, i) => (
                            <div key={i}>+ {m.name}{m.price > 0 ? (
                              discountEnabled
                                ? <span> (<s style={{color:'#999'}}>${m.price.toFixed(2)}</s> ${(m.price * discountRate).toFixed(2)})</span>
                                : ` (+$${m.price.toFixed(2)})`
                            ) : ''}</div>
                          ))}
                        </div>
                      )}
                      {item.note && (
                        <div className="summary-item-note">{t('single_meal.note_label', '備註')}: {item.note}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="summary-row" style={{ paddingBottom: 8 }}>
              <span>{t('payment.cutlery', '是否需要餐具')}</span>
              <span style={{ color: needCutlery ? '#2F7D5E' : '#ef4444', fontWeight: 500 }}>
                {needCutlery ? t('payment.cutlery_yes', '需要') : t('payment.cutlery_no', '不需要')}
              </span>
            </div>

            <div className="summary-totals">
              {discountEnabled ? (
                <>
                  <div className="summary-row">
                    <span>{t('payment.subtotal')}</span>
                    <span style={{textDecoration:'line-through', color:'#999'}}>CAD$ {orderBreakdown.originalSubtotal.toFixed(2)}</span>
                  </div>
                  <div className="summary-row" style={{color:'#e53e3e'}}>
                    <span>{globalDiscount.label || t('single_meal.discount', '全單折扣')}</span>
                    <span>-CAD$ {orderBreakdown.discount.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>{t('meal_selection.discounted_subtotal', '折後小計')}</span>
                    <span>CAD$ {orderBreakdown.subtotal.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <div className="summary-row">
                  <span>{t('payment.subtotal')}</span>
                  <span>CAD$ {orderBreakdown.subtotal.toFixed(2)}</span>
                </div>
              )}
              {orderType === 'delivery' && (
                <div className="summary-row fee-row">
                  <span>{t('payment.delivery_fee')}</span>
                  <span style={{color: orderBreakdown.deliveryFee === 0 ? '#166534' : 'inherit'}}>
                    {orderBreakdown.deliveryFee === 0 ? t('payment.free') : `CAD$ ${orderBreakdown.deliveryFee.toFixed(2)}`}
                  </span>
                </div>
              )}
              {orderBreakdown.packagingFee > 0 && (
                <div className="summary-row fee-row">
                  <span>{t('payment.packaging_fee', '外賣包裝費')}</span>
                  <span>CAD$ {orderBreakdown.packagingFee.toFixed(2)}</span>
                </div>
              )}
              <div className="summary-row tax-row">
                <span>GST (5%)</span>
                <span>CAD$ {orderBreakdown.gst.toFixed(2)}</span>
              </div>
              <div className="summary-row tax-row">
                <span>PST (7%)</span>
                <span>CAD$ {orderBreakdown.pst.toFixed(2)}</span>
              </div>
              <div className="summary-total">
                <span>{t('payment.total')}</span>
                <span>CAD$ {orderBreakdown.grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const SingleConfirmPage = () => (
  <Elements stripe={stripePromise}>
    <SingleConfirmPageInner />
  </Elements>
);

export default SingleConfirmPage;
